import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Paperclip, Trash2, FileImage, FileText, Upload, Loader2, Save, X, CheckCheck,
  ArrowDownWideNarrow, ArrowUpNarrowWide,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const MAX_LAB_FILE_BYTES = 20 * 1024 * 1024;

function uid() {
  try { return crypto.randomUUID(); } catch { return Math.random().toString(36).slice(2); }
}
function stripExt(name) {
  return String(name || "").replace(/\.[^.]+$/, "");
}

const isPdfFile = (file) => (file?.type || "") === "application/pdf" || /\.pdf$/i.test(file?.name || "");
const attIsPdf = (att) => (att?.mime_type === "application/pdf") || /\.pdf$/i.test(att?.filename || "");

// Lab files are served from a token-protected streaming endpoint (image_url) —
// the list no longer inlines the base64 (data_url) because a patient with many
// scans produced a several-hundred-MB response. A bare <img/iframe src=image_url>
// can't send the auth header, so (like the queue announcer's audio) we fetch the
// blob with the token and render that. Older/prescription-mode rows that still
// carry data_url inline are used directly.
function useAuthBlob(att) {
  const direct = att?.data_url || "";
  const remote = att?.image_url || "";
  const [src, setSrc] = useState(direct);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (direct) { setSrc(direct); setFailed(false); return undefined; }
    if (!remote) { setFailed(true); return undefined; }
    let objUrl = "";
    let cancelled = false;
    setSrc(""); setFailed(false);
    (async () => {
      try {
        const res = await fetch(remote, {
          headers: { Authorization: `Bearer ${localStorage.getItem("rxp_token") || ""}` },
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setSrc(objUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [direct, remote]);

  return { src, failed };
}

function AuthImage({ att, className, iconSize = 32 }) {
  const { src, failed } = useAuthBlob(att);
  if (failed || !src) return <FileImage size={iconSize} className="text-slate-400" />;
  return <img src={src} alt={att?.title || att?.filename || ""} className={className} />;
}

function AuthPdf({ att }) {
  const { src, failed } = useAuthBlob(att);
  if (failed) return <div className="text-sm text-slate-500 p-8">Could not load PDF.</div>;
  if (!src) return <Loader2 className="animate-spin text-slate-400" size={40} />;
  return (
    <div className="w-full">
      <iframe title={att?.title || att?.filename || "PDF"} src={src} className="w-full h-[66vh] rounded border border-slate-200 bg-white" />
      <div className="mt-2 text-center">
        <a href={src} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Open in new tab</a>
      </div>
    </div>
  );
}

export default function LabAttachmentsPanel({
  prescriptionId = "",
  patientId = "",
  attachments = [],
  onChanged,
  compact = false,
}) {
  const fileRef = useRef(null);
  const [staged, setStaged] = useState([]);        // [{id, file, url, title}]
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [viewer, setViewer] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);

  const [sortDir, setSortDir] = useState("desc");   // newest first by default

  const isPatientMode = !!patientId;

  // Always ordered by date. Prescription-mode attachments arrive in raw array
  // (upload) order, so sorting here covers both modes. Undated items sink to
  // the bottom either way rather than jumping to the top when ascending.
  const list = useMemo(() => {
    const raw = Array.isArray(attachments) ? attachments : [];
    const dateOf = (a) => a?.uploaded_at || a?.created_at || a?.date || "";
    return [...raw].sort((a, b) => {
      const da = dateOf(a);
      const db_ = dateOf(b);
      if (!da && !db_) return 0;
      if (!da) return 1;
      if (!db_) return -1;
      return sortDir === "desc" ? db_.localeCompare(da) : da.localeCompare(db_);
    });
  }, [attachments, sortDir]);

  const uploadUrl = isPatientMode
    ? `/patients/${patientId}/lab-results`
    : `/prescriptions/${prescriptionId}/lab-attachments`;
  const itemUrl = (aid) => isPatientMode
    ? `/patients/${patientId}/lab-results/${aid}`
    : `/prescriptions/${prescriptionId}/lab-attachments/${aid}`;

  // ---- Staging (pick many, review, rename, then upload all) ----
  const onPick = () => fileRef.current?.click();

  const onSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = "";
    const next = [];
    for (const f of files) {
      if (!f.type.startsWith("image/") && !isPdfFile(f)) { toast.error(`${f.name}: only image or PDF files are supported`); continue; }
      if (f.size > MAX_LAB_FILE_BYTES) { toast.error(`${f.name}: too large (max 20 MB)`); continue; }
      next.push({ id: uid(), file: f, url: URL.createObjectURL(f), title: stripExt(f.name), pdf: isPdfFile(f) });
    }
    if (next.length) setStaged((prev) => [...prev, ...next]);
  };

  const setStagedTitle = (id, title) => setStaged((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
  const removeStaged = (id) => setStaged((prev) => {
    const s = prev.find((x) => x.id === id);
    if (s) URL.revokeObjectURL(s.url);
    return prev.filter((x) => x.id !== id);
  });
  const clearStaged = () => { staged.forEach((s) => URL.revokeObjectURL(s.url)); setStaged([]); };

  const uploadAll = async () => {
    if (!staged.length) return;
    setUploading(true);
    setProgress({ done: 0, total: staged.length });
    let ok = 0;
    for (let i = 0; i < staged.length; i++) {
      const s = staged[i];
      try {
        const fd = new FormData();
        fd.append("file", s.file);
        if (s.title.trim()) fd.append("title", s.title.trim());
        await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        toast.error(`${s.title || s.file.name}: ${formatErr(err?.response?.data?.detail || err)}`);
      }
      setProgress({ done: i + 1, total: staged.length });
    }
    staged.forEach((s) => URL.revokeObjectURL(s.url));
    setStaged([]);
    setUploading(false);
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    if (onChanged) await onChanged();
  };

  // ---- Existing items ----
  const openViewer = (att) => { setViewer(att); setEditTitle(att.title || ""); };
  const saveTitle = async () => {
    if (!viewer) return;
    setSavingTitle(true);
    try {
      await api.put(itemUrl(viewer.id), { title: editTitle.trim() });
      toast.success("Title updated");
      setViewer({ ...viewer, title: editTitle.trim() });
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally { setSavingTitle(false); }
  };
  const remove = async (aid) => {
    if (!window.confirm("Remove this lab result?")) return;
    try {
      await api.delete(itemUrl(aid));
      toast.success("Lab result removed");
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
  };

  return (
    <>
      <Card className={`p-4 border-slate-200 ${compact ? "" : "mb-3"}`} data-testid="lab-attachments-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Paperclip size={16} className="text-blue-700" />
            <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>
              {isPatientMode ? "Lab Results / Imaging" : "Laboratory Attachments"}
            </h3>
            <span className="text-xs text-slate-500">{list.length} file{list.length === 1 ? "" : "s"}</span>
          </div>
          <div className="flex items-center gap-2">
            {list.length > 1 ? (
              <Button
                onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                size="sm"
                variant="outline"
                title={sortDir === "desc" ? "Newest first — click for oldest first" : "Oldest first — click for newest first"}
                data-testid="btn-sort-lab"
              >
                {sortDir === "desc"
                  ? <ArrowDownWideNarrow size={14} className="mr-1" />
                  : <ArrowUpNarrowWide size={14} className="mr-1" />}
                {sortDir === "desc" ? "Newest first" : "Oldest first"}
              </Button>
            ) : null}
            <Button onClick={onPick} size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={uploading} data-testid="btn-upload-lab">
              <Upload size={14} className="mr-1" /> Add Files
            </Button>
          </div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onSelect} data-testid="lab-file-input" />
        </div>

        {/* Staging area: review + rename before uploading */}
        {staged.length > 0 ? (
          <div className="mb-4 border border-blue-200 bg-blue-50/40 rounded-lg p-3" data-testid="lab-staging">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-sm font-semibold text-slate-800">Review {staged.length} file{staged.length === 1 ? "" : "s"} — rename, then upload</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={clearStaged} disabled={uploading}>Clear</Button>
                <Button size="sm" onClick={uploadAll} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-upload-all">
                  {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCheck size={14} className="mr-1" />}
                  {uploading ? `Uploading ${progress.done}/${progress.total}…` : `Upload all (${staged.length})`}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {staged.map((s) => (
                <div key={s.id} className="bg-white border border-slate-200 rounded-md p-2 flex gap-2 items-start" data-testid={`lab-staged-${s.id}`}>
                  {s.pdf ? (
                    <div className="w-16 h-16 rounded border border-slate-200 shrink-0 bg-red-50 flex flex-col items-center justify-center text-red-600">
                      <FileText size={22} />
                      <span className="text-[9px] font-bold mt-0.5">PDF</span>
                    </div>
                  ) : (
                    <img src={s.url} alt={s.title} className="w-16 h-16 object-cover rounded border border-slate-200 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <Input value={s.title} onChange={(e) => setStagedTitle(s.id, e.target.value)} placeholder="Name (e.g. CBC, Chest X-ray)" className="h-8 text-sm" disabled={uploading} />
                    <div className="text-[10px] text-slate-400 mt-1 truncate">{s.file.name} · {Math.round(s.file.size / 1024)} KB</div>
                  </div>
                  <button type="button" onClick={() => removeStaged(s.id)} disabled={uploading} className="text-slate-400 hover:text-red-600 shrink-0" title="Remove">
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {list.length === 0 && staged.length === 0 ? (
          <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-md p-4 text-center bg-slate-50">
            Upload lab results or imaging (CBC, X-ray, ECG, ultrasound, CT, MRI…) as images or PDF. You can select several files at once, rename each, then upload them all together.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {list.map((att) => (
              <div key={att.id} className="border border-slate-200 rounded-md overflow-hidden bg-white group relative" data-testid={`lab-thumb-${att.id}`}>
                <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center cursor-pointer" onClick={() => openViewer(att)} title="Click to view / rename" data-testid={`lab-open-${att.id}`}>
                  {attIsPdf(att) ? (
                    <div className="flex flex-col items-center text-red-600">
                      <FileText size={30} />
                      <span className="text-[9px] font-bold mt-0.5">PDF</span>
                    </div>
                  ) : att.mime_type?.startsWith("image/") ? (
                    <AuthImage att={att} className="w-full h-full object-contain" iconSize={32} />
                  ) : (
                    <FileImage size={32} className="text-slate-400" />
                  )}
                </div>
                <div className="p-2 text-xs">
                  <div className="font-medium text-slate-900 truncate" title={att.title || att.filename}>{att.title || att.filename}</div>
                  {att.title ? <div className="text-slate-500 truncate text-[10px] mono">{att.filename}</div> : null}
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {att.size_bytes ? `${Math.round(att.size_bytes / 1024)} KB` : ""}
                    {att.uploaded_at ? ` · ${(att.uploaded_at || "").slice(0, 10)}` : ""}
                  </div>
                  {att.uploaded_by ? <div className="text-[10px] text-slate-400 truncate">Uploaded by {att.uploaded_by}</div> : null}
                </div>
                <button type="button" onClick={() => remove(att.id)} className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 text-red-600 rounded-full w-7 h-7 flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition" data-testid={`btn-remove-lab-${att.id}`} title="Remove">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-3xl" data-testid="lab-viewer-dialog">
          <DialogHeader><DialogTitle>Lab Result / Imaging</DialogTitle></DialogHeader>
          {viewer ? (
            <div className="space-y-3">
              <div className="bg-slate-100 rounded-md p-2 flex justify-center max-h-[72vh] overflow-auto">
                {attIsPdf(viewer) ? (
                  <AuthPdf att={viewer} />
                ) : viewer.mime_type?.startsWith("image/") ? (
                  <AuthImage att={viewer} className="max-w-full max-h-[68vh] object-contain" iconSize={48} />
                ) : (
                  <FileImage size={48} className="text-slate-400" />
                )}
              </div>
              <div>
                <label className="text-xs text-slate-500">Title</label>
                <div className="flex gap-2 mt-1">
                  <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="e.g. CBC, Chest X-ray, ECG" data-testid="lab-edit-title" />
                  <Button onClick={saveTitle} disabled={savingTitle} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-lab-title">
                    {savingTitle ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Save
                  </Button>
                </div>
                <div className="text-[11px] text-slate-400 mt-1 truncate">
                  {viewer.filename}
                  {viewer.size_bytes ? ` · ${Math.round(viewer.size_bytes / 1024)} KB` : ""}
                  {viewer.uploaded_by ? ` · Uploaded by ${viewer.uploaded_by}` : ""}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
